import { format, isToday, isYesterday } from "date-fns";
import LogManager from "./logManager";
import { callRouterLambda } from "./routerLambdaUtils";
import { SESSION_STORAGE_KEYS } from "../constants";

/**
 * Fetches summaries from the backend between specified time range or defaults to last 30 days.
 * Falls back to empty list on failure.
 *
 * @param {number} [queryStartTime] - Optional start time for query (in epoch seconds)
 * @param {number} [queryEndTime] - Optional end time for query (in epoch seconds)
 * @param {number|string} [count=30] - Optional number of summaries to fetch
 * @returns {Promise<{ summaries: Array }>}
 */
export const fetchSummaries = async (queryStartTime, queryEndTime, count = 30) => {
  // If specific time range is provided, use it; otherwise use default 30-day range
  const useSpecificTimeRange = queryStartTime !== undefined && queryEndTime !== undefined;
  
  const queryEndEpochSec = useSpecificTimeRange ? queryEndTime : Math.floor(Date.now() / 1000);
  const queryStartEpochSec = useSpecificTimeRange ? queryStartTime : queryEndEpochSec - 30 * 24 * 60 * 60;

  // Build the request with the provided or default parameters
  const request = {
    queryStartTime: queryStartEpochSec,
    queryEndTime: queryEndEpochSec,
    count: count.toString(),
  };

  try {
    const summaryListJson = await callRouterLambda("/summaries", request);

    console.log("The summaryListJson is:", summaryListJson);
    console.log("Received summaries successfully from backend for request" + JSON.stringify(request));
    return summaryListJson;
  } catch (error) {
    LogManager.error("Failed to fetch summaries:", error);
    throw error;
  }
};

/**
 * Fetches paginated summaries from the backend.
 *
 * @param {boolean} [preFetch=false] - Whether this is a pre-fetch operation
 * @param {string} [lastEvaluatedKey] - Key from previous query to fetch next page
 * @param {number} [pageSize=5] - Number of summaries to fetch per page
 * @param {Object} [stateSetters] - Optional React state setters
 * @param {Function} [stateSetters.setSummaryGroups] - Function to update summary groups state
 * @param {Function} [stateSetters.setRawSummaries] - Function to update raw summaries state
 * @param {Function} [stateSetters.setLastEvaluatedKey] - Function to update lastEvaluatedKey state
 * @param {Function} [stateSetters.setIsEndOfData] - Function to update isEndOfData state
 * @returns {Promise<{ summaryListJson: Array, newLastEvaluatedKey: string|null, isEndOfData: boolean }>}
 */
export const fetchPaginatedSummaries = async (
  preFetch = false, 
  lastEvaluatedKey = null, 
  pageSize = 5,
  stateSetters = {}
) => {
  const isEndOfData = sessionStorage.getItem(SESSION_STORAGE_KEYS.IS_END_OF_DATA) === "true";
  if (isEndOfData) {
    return {
      summaryListJson: [],
      newLastEvaluatedKey: null,
      isEndOfData: true
    };
  }

  const request = {
    count: pageSize.toString(),
    pagination: true
  };

  if (lastEvaluatedKey) {
    request.lastEvaluatedKey = JSON.stringify(lastEvaluatedKey);
  }

  try {
    const payload = await callRouterLambda("/summaries", request);

    const summaryListJson = payload.summaries || [];
    const newLastEvaluatedKey = payload.lastEvaluatedKey;

    // Determine if this is the end of data
    const newIsEndOfData = !newLastEvaluatedKey;

    // Update cache based on different scenarios
    if (preFetch || (!lastEvaluatedKey && summaryListJson.length > 0)) {
      // Pre-fetch or initial load
      const groups = generateSummaryGroup(summaryListJson);
      sessionStorage.setItem(SESSION_STORAGE_KEYS.SUMMARY_CACHE, JSON.stringify(groups));
      sessionStorage.setItem(SESSION_STORAGE_KEYS.LAST_EVALUATED_KEY, JSON.stringify(newLastEvaluatedKey));
      sessionStorage.setItem(SESSION_STORAGE_KEYS.RAW_SUMMARIES, JSON.stringify(summaryListJson));
      sessionStorage.setItem(SESSION_STORAGE_KEYS.IS_END_OF_DATA, newIsEndOfData.toString());
      
      // Update React state if setters are provided
      if (stateSetters.setSummaryGroups) stateSetters.setSummaryGroups(groups);
      if (stateSetters.setRawSummaries) stateSetters.setRawSummaries(summaryListJson);
      if (stateSetters.setLastEvaluatedKey) stateSetters.setLastEvaluatedKey(newLastEvaluatedKey);
      if (stateSetters.setIsEndOfData) stateSetters.setIsEndOfData(newIsEndOfData);
    } else if (lastEvaluatedKey && summaryListJson.length > 0) {
      // Pagination
      const existingRawSummaries = JSON.parse(sessionStorage.getItem(SESSION_STORAGE_KEYS.RAW_SUMMARIES) || "[]");
      const newRawSummaries = [...existingRawSummaries, ...summaryListJson];
      const newGroups = generateSummaryGroup(newRawSummaries);
      
      sessionStorage.setItem(SESSION_STORAGE_KEYS.SUMMARY_CACHE, JSON.stringify(newGroups));
      sessionStorage.setItem(SESSION_STORAGE_KEYS.LAST_EVALUATED_KEY, JSON.stringify(newLastEvaluatedKey));
      sessionStorage.setItem(SESSION_STORAGE_KEYS.RAW_SUMMARIES, JSON.stringify(newRawSummaries));
      sessionStorage.setItem(SESSION_STORAGE_KEYS.IS_END_OF_DATA, newIsEndOfData.toString());
      
      // Update React state if setters are provided
      if (stateSetters.setSummaryGroups) stateSetters.setSummaryGroups(newGroups);
      if (stateSetters.setRawSummaries) stateSetters.setRawSummaries(newRawSummaries);
      if (stateSetters.setLastEvaluatedKey) stateSetters.setLastEvaluatedKey(newLastEvaluatedKey);
      if (stateSetters.setIsEndOfData) stateSetters.setIsEndOfData(newIsEndOfData);
    }

    // Handle case when no summaries are returned
    if (summaryListJson.length === 0) {
      sessionStorage.setItem(SESSION_STORAGE_KEYS.IS_END_OF_DATA, "true");
      if (stateSetters.setIsEndOfData) stateSetters.setIsEndOfData(true);
    }

    return {
      summaryListJson: payload.summaries || [],
      newLastEvaluatedKey: payload.lastEvaluatedKey,
      isEndOfData: newIsEndOfData
    };
  } catch (error) {
    LogManager.error("Failed to fetch paginated summaries:", error);
    throw error;
  }
};

/**
 * Preprocesses the summary list by grouping summaries into
 * Today, Yesterday, or a formatted date string (e.g., Friday, October 4),
 * and returns them in descending date order (most recent first).
 *
 * Used by Summaries tab in meeting
 * @param {Array} summaryListJson - List of summaries
 * @returns {Object} - Ordered { [groupLabel]: summary[] }
 */
export const generateSummaryGroup = (summaryListJson) => {
  const summaryGroups = {};

  summaryListJson.forEach((summary) => {
    if (!summary.meetingStartTime) {
      console.warn("Missing meetingStartTime in summary:", summary);
      return;
    }

    const date = new Date(summary.meetingStartTime * 1000);
    let groupKey;

    if (isToday(date)) {
      groupKey = "Today";
    } else if (isYesterday(date)) {
      groupKey = "Yesterday";
    } else {
      groupKey = format(date, "EEEE, MMMM d"); // e.g., "Friday, October 4"
    }

    if (!summaryGroups[groupKey]) {
      summaryGroups[groupKey] = [];
    }

    summaryGroups[groupKey].push(summary);
  });

  // Sort summaries within each group by descending meetingStartTime
  Object.values(summaryGroups).forEach((list) =>
    list.sort((a, b) => b.meetingStartTime - a.meetingStartTime)
  );

  // Compute latest meetingStartTime for each group to sort the keys
  const groupEntries = Object.entries(summaryGroups).map(([key, list]) => {
    const latestTime = Math.max(...list.map((s) => s.meetingStartTime));
    return [key, list, latestTime];
  });

  // Sort groups by latest meeting time (descending)
  groupEntries.sort((a, b) => b[2] - a[2]);

  // Rebuild the sorted group object
  const sortedGroups = {};
  groupEntries.forEach(([key, list]) => {
    sortedGroups[key] = list;
  });

  return sortedGroups;
};

/**
 * Paginates the grouped summaries into pages of 4 summaries each,
 * preserving the group title with each chunk.
 *
 * Used by Summaries tab in client
 *
 * @param {Object} groups - Grouped summaries, e.g. { "Today": [..], "Yesterday": [..] }
 * @returns {Array<{ groupTitle: string, summaries: Array }>}
 */
export const preProcessInClient = (groups) => {
  const newPages = [];

  Object.entries(groups).forEach(([groupTitle, summaries]) => {
    for (let i = 0; i < summaries.length; i += 4) {
      newPages.push({
        groupTitle,
        summaries: summaries.slice(i, i + 4),
      });
    }
  });

  return newPages;
};

/**
 * Truncates attendee names to fit within 250px width.
 * @param names - Array of attendee name strings
 * @returns A string trimmed with "..." if it exceeds 250px width
 */
export const truncateAttendees = (names) => {
  const maxWidth = 250;

  // Create an offscreen measurement element
  const span = document.createElement("span");
  span.style.fontSize = "12px";
  span.style.fontStyle = "normal";
  span.style.fontWeight = "400";
  span.style.lineHeight = "16px";
  span.style.position = "absolute";
  span.style.visibility = "hidden";
  span.style.whiteSpace = "nowrap";
  span.style.pointerEvents = "none";
  span.style.left = "-9999px";
  document.body.appendChild(span);

  let result = "";

  for (let i = 0; i < names.length; i++) {
    const candidate = result ? `${result}, ${names[i]}` : names[i];
    span.textContent = candidate;
    if (span.offsetWidth > maxWidth) {
      result += "...";
      break;
    } else {
      result = candidate;
    }
  }

  document.body.removeChild(span);
  return result;
}
